import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import {
  Carousel,
  Spinner,
  Button,
  Table,
} from 'react-bootstrap';
import { fetchComponentsInitAction, updateSelectedComponentAction } from '../../../../../actions/instances/components';
import { setSelectedBuildingAction } from '../../../../../actions/instances/buildings';
import BasePage from '../../../BasePage/BasePage';
import LiveInput from '../../../../atoms/LiveInput/LiveInput';
import Icon from '../../../../atoms/Icon/Icon';
import Sidebar from '../../../../molecules/Sidebar/Sidebar';
import Api from '../../../../../services/Api';
import DeleteModal from '../../../../molecules/DeleteModal/DeleteModal';
import { loadComponentsMetadata } from '../../../../../helpers/metadata';
import DocumentsList from '../../../../Organisms/DocumentsList/DocumentsList';
import CitationList from '../../../../Organisms/CitationList/CitationList';
import ElementsList from '../../../../Organisms/ElementsList/ElementsList';
import { publishComponent } from '../../../../../helpers/publish';
import InstanceMetadata from '../../../../Organisms/InstanceMetadata/InstanceMetadata';
import CharacteristicsCardList from '../../../../Organisms/CharacteristicsCardList/CharacteristicsCardList';
import ContextsList from '../../../../Organisms/ContextsList/ContextsList';
import './EditViewComponent.scss';

const EditViewComponent = () => {
  const {
    selectedComponent,
  } = useSelector(state => state.buildingComponentInstance);
  const { user } = useSelector(state => state.auth);
  const history = useHistory();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalShown, setIsDeleteModalShown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [elements, setElements] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [characteristics, setCharacteristics] = useState([]);
  const [isPublishing, setIsPublishing] = useState(false);
  const loadMetadata = async () => {
    const metadata = await loadComponentsMetadata(selectedComponent.id);
    setElements(metadata.elements);
    setBuildings(metadata.buildings);
    setCharacteristics(metadata.characteristics);
    setContexts(metadata.contexts);
  };
  useEffect(() => {
    setIsLoading(true);
    loadMetadata().then(() => setIsLoading(false));
  }, []);
  const dispatch = useDispatch();
  if (!selectedComponent) {
    history.push('/instances/building-components');
  }

  const deleteComponent = () => {
    setIsDeleting(true);
    Api.delete(`/instances/building-components/${selectedComponent.id}`).then((response) => {
      if (response.status === 204) {
        dispatch(fetchComponentsInitAction());
        setTimeout(() => {
          history.goBack();
          setIsDeleting(false);
        }, 500);
      } else {
        setIsDeleting(false);
      }
    });
  };
  const syncElements = async (newElement) => {
    setIsLoading(true);
    const elementDetails = await Api.get(`instances/building-elements/${newElement.id}`);
    const newIds = [
      ...elementDetails.data.components.map(x => x.id),
      selectedComponent.id,
    ];
    await Api.patch(`instances/building-elements/${newElement.id}`, {
      components_ids: newIds,
    });
    const response = await loadComponentsMetadata(selectedComponent.id);
    setElements(response.elements);
    setBuildings(response.buildings);
    setIsLoading(false);
  };
  const syncCharacteristics = async (newCharacteristic) => {
    setIsLoading(true);
    const componentCharacteristic = await Api.get(`instances/building-components/${selectedComponent.id}`);
    const newIds = [
      ...componentCharacteristic.data.characteristics.map(x => x.id),
      newCharacteristic.id,
    ];
    await Api.patch(`instances/building-components/${selectedComponent.id}`, {
      characteristics_ids: newIds,
    });
    const response = await loadComponentsMetadata(selectedComponent.id);
    setElements(response.elements);
    setBuildings(response.buildings);
    setCharacteristics(response.characteristics);
    setIsLoading(false);
  };
  const syncContexts = async (newContext) => {
    setIsLoading(true);
    const ids = contexts.map(x => x.id);
    await Api.patch(`instances/building-contexts/${selectedComponent.id}`, {
      contexts_ids: [...ids, newContext.id],
    });
    await loadMetadata();
    setIsLoading(false);
  };
  return (
    <BasePage>
      <DeleteModal
        onDelete={deleteComponent}
        isShown={isDeleteModalShown}
        isLoading={isDeleting}
        onClose={() => setIsDeleteModalShown(false)}
        isAllowed={elements && elements.length === 0}
      >
        Are you sure you want to delete
        {' '}
        <strong>{selectedComponent.name}</strong>
        {'? '}
        <br />
        This action cannot be undone.
      </DeleteModal>
      <div className="col-md-3">
        <div className="row">
          <div className="col-md-12">
            {selectedComponent.images && selectedComponent.images.length > 0 ? (
              <Carousel>
                {selectedComponent.images.map(image => (
                  <Carousel.Item>
                    <img
                      className="d-block w-100 img-fluid"
                      src={image.url}
                      alt={image.label}
                      style={{
                        maxHeight: '400px',
                        objectFit: 'cover',
                      }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            ) : (
              <img src="/images/default.jpg" alt={selectedComponent.name} className="img-fluid" />
            )}
          </div>
          <div className="col-md-12 mt-2 d-grid gap-2">
            {user.role_id !== 3 && (
              <Button
                variant={selectedComponent.in_review === 1 ? 'success' : 'warning'}
                onClick={async () => {
                  setIsPublishing(true);
                  const component = await publishComponent(
                    selectedComponent.id,
                    selectedComponent.in_review,
                  );
                  dispatch(updateSelectedComponentAction(component));
                  dispatch(fetchComponentsInitAction());
                  setIsPublishing(false);
                }}
              >
                {isPublishing ? (
                  <>
                    <Spinner
                      as="span"
                      animation="grow"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="ms-2">Loading...</span>
                  </>
                ) : (
                  <>
                    <Icon icon="check" />
                    <span className={`ms-2 ${selectedComponent.in_review === 0 ? 'text-white' : ''}`}>
                      {selectedComponent.in_review === 1 ? 'Approve' : 'Disapprove'}
                    </span>
                  </>
                )}
              </Button>
            )}
            <Button
              variant="dark"
              onClick={() => {
                dispatch(updateSelectedComponentAction(selectedComponent));
                history.push('/instances/building-components/edit');
              }}
            >
              <Icon icon="edit" />
              <span className="ms-2">Edit</span>
            </Button>
            <Button variant="dark" onClick={() => history.goBack()}>
              <Icon icon="times" />
              <span className="ms-2">Cancel</span>
            </Button>
            {user.role_id !== 3 && (
              <Button variant="danger" onClick={() => setIsDeleteModalShown(true)}>
                {isDeleting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="grow"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="ms-2">Loading...</span>
                  </>
                ) : (
                  <>
                    <Icon icon="trash" />
                    <span className="ms-2">Delete</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="col-md-5">
        <div className="row d-flex align-items-center mb-3">
          <div className="col-md-1 text-end">
            <Icon icon="edit" color="#000" />
          </div>
          <div className="col-md-11 text-start">
            <LiveInput endpoint="/instances/building-components" fieldName="name" id={selectedComponent.id} value={selectedComponent.name} fontSize={22} />
          </div>
        </div>
        <div className="row border-top border-bottom mt-2 p-2">
          <div className="col-md-12">
            <div className="border-end d-inline pe-2 me-2">
              <span className="me-1">
                <Icon icon="calendar" color="#000" />
              </span>
              {moment(selectedComponent.cerated_at).format('DD-MM-YYYY')}
            </div>
            <div className="d-inline">
              {selectedComponent.tags.map(tag => (
                <span className="me-2 border-end pe-2">
                  <Icon icon="tag" color="#000" />
                  <span className="ms-2">
                    {tag.name}
                  </span>
                </span>
              ))}
            </div>
            <div className="d-inline border-end d-inline pe-2 me-2">
              <Icon icon="map" color="#000" />
              <span className="ms-2">
                {`${selectedComponent.city.name}, ${selectedComponent.city.country.name} [${selectedComponent.city.code}-${selectedComponent.city.country.code}]`}
              </span>
            </div>
            <div className="d-inline">
              <Icon icon="globe" color="#000" />
              <span className="ms-2">
                {`${selectedComponent.dynasty.name} [${selectedComponent.dynasty.code}]`}
              </span>
            </div>
          </div>
        </div>
        <InstanceMetadata instance={selectedComponent} title="Component metadata" />
        <div className="row mb-5 mt-3">
          <h4>Buildings</h4>
          <br />
          <Table responsive striped hover>
            <thead>
              <tr>
                <td />
                <td>Building</td>
                <td>Dynasty</td>
              </tr>
            </thead>
            <tbody>
              {buildings && buildings.length > 0 ? (
                buildings.map(building => (
                  <tr>
                    <td>
                      {building.images.length > 0 ? (
                        <img alt={building.name} src={building.images[0].url} className="img-fluid" height={50} width={50} />
                      ) : (
                        <img alt={building.name} src="/images/default.jpg" className="img-fluid" height={50} width={50} />
                      )}
                    </td>
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                    <td
                      onClick={() => {
                        dispatch(setSelectedBuildingAction(building));
                        history.push('/buildings/view');
                        window.scrollTo(0, 0);
                      }}
                    >
                      {`${building.name} #${building.number}`}
                    </td>
                    <td>{`${building.dynasty.name} [${building.dynasty.code}]`}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center">No buildings with this component were found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        <ElementsList
          elements={elements}
          onItemAdded={element => syncElements(element)}
          isLoading={isLoading}
          cityId={selectedComponent.city.id}
          dynastyId={selectedComponent.dynasty.id}
        />
        <ContextsList
          contexts={contexts}
          isLoading={isLoading}
          onItemAdded={con => syncContexts(con)}
          onItemelet={con => syncContexts(con)}
          cityId={selectedComponent.city.id}
          dynastyId={selectedComponent.dynasty.id}
        />
        <DocumentsList documents={selectedComponent ? selectedComponent.documents : []} title="Component Documents" />
        <CitationList citations={selectedComponent ? selectedComponent.citation : []} title="Component Citations" />
      </div>
      <div className="col-md-3 mt-5">
        <CharacteristicsCardList
          characteristics={characteristics}
          cityId={selectedComponent.city.id}
          dynastyId={selectedComponent.dynasty.id}
          isLoading={isLoading}
          onItemAdded={char => syncCharacteristics(char)}
        />
        <Sidebar />
      </div>
    </BasePage>
  );
};

export default EditViewComponent;
